import _ from 'lodash';
import {find} from 'lib/objectHelper';
import {DUAL_CADDY_TRAY_CADDY_LOCATIONS, GENERAL_TRAY_CADDY_LOCATIONS, TaskType, TrayType} from '../constants/dataEnum';
import {filterToArray} from './objectHelper';
import {getFirstSelectedCard} from './cardAndTaskHelper';

export const getTotalTipVolumeOnCard = (selectedCardTasks, addedTask) => {
  const {volume: addedVolume, type: addedTaskType, ID: addedTaskId} = addedTask || {};
  const updatedVolume = addedTaskType === TaskType.DISPENSE ? -addedVolume : addedVolume;

  let pipettingVolume = selectedCardTasks.reduce((vol, taskItem) => {
    if (addedTaskId && (taskItem.isIgnored || taskItem.ID === addedTaskId)) return parseFloat(vol);

    switch (taskItem.type) {
      case TaskType.ASPIRATE:
        return parseFloat(vol) + parseFloat(taskItem.volume);
      case TaskType.DISPENSE:
        return parseFloat(vol) - parseFloat(taskItem.volume);
      default:
        return parseFloat(vol);
    }
  }, 0);

  pipettingVolume += parseFloat(updatedVolume);
  return parseFloat(parseFloat(pipettingVolume).toFixed(1));
};

// #region tip volume and aspirate sequence checking
const reduceTipVolumeSequence = (dispenseTask, tipVolumeSequence) => {
  const updatedAspirateSequence = [...tipVolumeSequence];
  const totalSequence = updatedAspirateSequence.length;

  let remainingVolumeToDispense = dispenseTask.volume;

  for (let idx = totalSequence - 1; idx >= 0; idx -= 1) {
    const sequence = updatedAspirateSequence[idx];

    const sequenceVolumeAfterDispensed = sequence.volume - Math.abs(remainingVolumeToDispense);

    if (sequenceVolumeAfterDispensed <= 0) {
      // current aspirate's sequence volume is not sufficient to dispense. dispense until empty

      // remove the insufficient sequence
      updatedAspirateSequence.pop();
      // set the remaining volume to be subtracted on the next sequence
      remainingVolumeToDispense = sequenceVolumeAfterDispensed;
    } else {
      // this aspirate sequence is sufficient for dispensing. set the remaining volume to the sequence's data
      sequence.volume = sequenceVolumeAfterDispensed;
      break;
    }
  }

  return updatedAspirateSequence;
};

const addTipVolumeSequence = (aspirateTask, tipVolumeSequence) => {
  const updatedAspirateSequence = [...tipVolumeSequence];
  const lastSequence = tipVolumeSequence[tipVolumeSequence.length - 1];

  // if aspirateTask has the same task type as last sequence (task can be aspirate or air gap), update the last sequence volume
  if (lastSequence?.type === aspirateTask.type) {
    lastSequence.volume += aspirateTask.volume;
  } else {
    updatedAspirateSequence.push({type: aspirateTask.type, volume: aspirateTask.volume});
  }

  return updatedAspirateSequence;
};

const getTipVolumeAndAspirateSequenceOptions = {newTask: null, isIncludeDispenseSequence: false};

/**
 * Returns squashed aspirate volume sequence
 * @param {Object} selectedCard selected card
 * @param {Object} tasks entire tasks object
 * @param {Object} opt options. see variable getTipVolumeAndAspirateSequenceOptions.
 * Available options are including additional task (newTask) and including dispense sequence (isIncludeDispenseSequence). By default only aspirate sequence will be shown.
 * @returns {Object} Object of total tip volume and volume sequence
 */
export const getTipVolumeAndAspirateSequence = (selectedCard, tasks, opt = getTipVolumeAndAspirateSequenceOptions) => {
  const {newTask, isIncludeDispenseSequence} = {
    ...getTipVolumeAndAspirateSequenceOptions,
    ...(opt || {}),
  };

  const selectedCardTasks = _.orderBy(
    filterToArray(tasks, {
      ID: selectedCard?.tasks,
    }),
    ['number'],
    ['asc'],
  );

  const taskIds = selectedCardTasks.map((item) => item.ID);

  // tip aspirate sequence is an array that consist of object { type, volume }
  // type can be either Aspirate or Air Gap
  let tipVolumeSequence = [];
  let tipVolume = 0;

  for (let index = 0; index < taskIds.length; index += 1) {
    const taskId = taskIds[index];
    const task = tasks[taskId];
    const taskVolume = parseFloat(task?.volume);

    // calculate current tip volume up until the new task's order
    const isTaskAfterNewTask = newTask && task.number > newTask?.number;

    if (task.ID === newTask?.ID) continue;
    if (task.isIgnored || !taskVolume) continue;
    if (task.type === TaskType.MIXING) continue;
    if (isTaskAfterNewTask) break;

    // calculate total tip volume
    if (task.type === TaskType.DISPENSE) {
      tipVolume = Number((tipVolume - taskVolume).toFixed(1));
    } else {
      tipVolume += taskVolume;
    }

    // handle volume sequence
    if (task.type !== TaskType.DISPENSE || isIncludeDispenseSequence) {
      tipVolumeSequence = addTipVolumeSequence(task, tipVolumeSequence);
    } else {
      tipVolumeSequence = reduceTipVolumeSequence(task, tipVolumeSequence);
    }
  }

  tipVolume = Math.max(tipVolume, 0);

  return {tipVolume, tipVolumeSequence};
};

export const getMaxMinTipVolumeAllowed = (
  tipVolume,
  maxAspirateVolume,
  opt = {airGap: {isExistsOnCard: false, isNewTask: false}, addingOrEditingMixingTask: false},
) => {
  let minimumAllowedVolume = Math.min(-1 * tipVolume, 0);
  let maximumAllowedVolume = 999;

  const {airGap, addingOrEditingMixingTask} = opt || {};

  if (airGap.isNewTask || addingOrEditingMixingTask) {
    minimumAllowedVolume = 0.1;
  }

  if (airGap.isExistsOnCard || airGap.isNewTask) {
    maximumAllowedVolume = tipVolume > maxAspirateVolume ? tipVolume : maxAspirateVolume - tipVolume;
  }
  if (addingOrEditingMixingTask) {
    maximumAllowedVolume = maxAspirateVolume;
  }

  return {maximumAllowedVolume, minimumAllowedVolume};
};

// #endregion tip volume and aspirate sequence checking

export const doesCardHaveOverAspiratesTask = (selectedCard, tasks, maxAspirateVolume, opt = {}) => {
  const {tipVolumeSequence} = getTipVolumeAndAspirateSequence(selectedCard, tasks, {
    isIncludeDispenseSequence: true,
    ...opt,
  });

  const overAspiratesTasks = tipVolumeSequence.find(
    (sequence) => sequence.type === TaskType.ASPIRATE && sequence.volume > maxAspirateVolume,
  );

  return overAspiratesTasks;
};

export const isInsertLocationOnCaddy = (insertLocation, trayType) => {
  if (trayType === TrayType.DOUBLE_CADDY) {
    return DUAL_CADDY_TRAY_CADDY_LOCATIONS.includes(insertLocation);
  }

  return GENERAL_TRAY_CADDY_LOCATIONS.includes(insertLocation);
};

export const mergeWellStyleByNest = (style1, style2) => {
  const mergedStyle = {};

  Object.keys(style1).forEach((nest) => {
    const style2NestWellStyle = style2[nest] || {};

    mergedStyle[nest] = {...style1[nest], ...style2NestWellStyle};
  });

  return mergedStyle;
};

export const mergeArrayOfWellStylesByNest = (array) => {
  let result = {};

  array.forEach((item) => {
    result = mergeWellStyleByNest(item, result);
  });

  return result;
};

export const getUncompleteMixingTask = () => (dispatch, getState) => {
  const {cardState} = getState();
  const selectedCard = getFirstSelectedCard(cardState.cards);

  const uncompleteMixingTask = find(cardState.tasks, {
    type: TaskType.MIXING,
    ID: selectedCard?.tasks,
    labwareID: undefined,
  });
  return uncompleteMixingTask;
};
